function debounce(fn, wait) {
  let t;
  return function (...args) {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

class CollectionFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.section;
    this.filterData = [];
    this.searchParamsPrev = window.location.search.slice(1);
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const form = this.querySelector("form");
    form.addEventListener("input", this.debouncedOnSubmit);
    form.addEventListener("change", this.debouncedOnSubmit);
  }

  connectedCallback() {
    setTimeout(() => {
      this.filterBySizeAvailability();
    }, 100);

    if (this.hasAttribute("data-custom-color-filter")) {
      this.addEventListener("click", (e) => {
        if (!e.target.closest("label[data-color-key]")) return;
        this._colorClickTime = Date.now();
        setTimeout(() => this.filterBySizeAvailability(), 10);
      });
    }

    // Re-applique les filtres quand l'infinite scroll ajoute des produits.
    // Sans ça, les pages 2+ s'affichent non filtrées sous les produits filtrés.
    this._onInfinitePageLoaded = (e) => {
      if (e.detail?.containerId !== "ProductGridContainer") return;
      this.filterBySizeAvailability();
    };
    document.addEventListener("infinite-page:loaded", this._onInfinitePageLoaded);
  }

  disconnectedCallback() {
    if (this._onInfinitePageLoaded) {
      document.removeEventListener("infinite-page:loaded", this._onInfinitePageLoaded);
      this._onInfinitePageLoaded = null;
    }
  }

  renderPage(searchParams, event, updateURLHash = true) {
    this.searchParamsPrev = searchParams;
    const sections = [this.sectionId];
    sections.forEach((sectionId) => {
      const url = `${window.location.pathname}?section_id=${sectionId}&${searchParams}`;
      const filterDataUrl = element => element.url === url;

      this.filterData.some(filterDataUrl) ?
        this.renderSectionFromCache(filterDataUrl, event) :
        this.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) this.updateURLHash(searchParams);
  }

  renderSectionFromFetch(url, event) {
    const productGridEl = document.getElementById("ProductGridContainer");
    if (productGridEl) {
      productGridEl.classList.add("loading");
    }
    fetch(url)
      .then(response => response.text())
      .then((responseText) => {
        const html = responseText;
        this.filterData = [...this.filterData.slice(-19), { html, url }];
        const parsedDoc = new DOMParser().parseFromString(html, "text/html");
        this.renderFilters(parsedDoc, event);
        this.renderProductGridHeader(parsedDoc);
        this.renderProductGridContainer(parsedDoc);
        this.renderFiltersMobileResults(parsedDoc);
      })
      .catch((error) => { console.error("collection-filters fetch error:", error); })
      .finally(() => {
        if (productGridEl) {
          productGridEl.classList.remove("loading");
        }
      });
  }

  renderSectionFromCache(filterDataUrl, event) {
    const html = this.filterData.find(filterDataUrl).html;
    const parsedDoc = new DOMParser().parseFromString(html, "text/html");
    this.renderFilters(parsedDoc, event);
    this.renderProductGridHeader(parsedDoc);
    this.renderProductGridContainer(parsedDoc);
    this.renderFiltersMobileResults(parsedDoc);
  }

  renderProductGridContainer(parsedDoc) {
    const el = document.getElementById("ProductGridContainer");
    const parsed = parsedDoc.getElementById("ProductGridContainer");
    if (el && parsed) {
      el.innerHTML = parsed.innerHTML;
      this.filterBySizeAvailability();
    }
  }

  renderProductGridHeader(parsedDoc) {
    const el = document.getElementById("ProductGridHeader");
    const parsed = parsedDoc.getElementById("ProductGridHeader");
    if (el && parsed) {
      el.innerHTML = parsed.innerHTML;
    }
  }

  renderFiltersMobileResults(parsedDoc) {
    const el = document.querySelector(".filters-mobile-results");
    const parsed = parsedDoc.querySelector(".filters-mobile-results");
    if (el && parsed) {
      el.innerHTML = parsed.innerHTML;
    }
  }

  renderFilters(parsedDoc, event) {
    const facetDetailsElements = parsedDoc.querySelectorAll("#CollectionFiltersForm .js-filter");
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest(".js-filter") : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };
    const isCustomColor = this.hasAttribute("data-custom-color-filter");
    const facetsToRender = Array.from(facetDetailsElements).filter(element => {
      if (matchesIndex(element)) return false;
      if (event && isCustomColor && element.classList.contains("js-filter-color-custom")) return false;
      return true;
    });

    facetsToRender.forEach((element) => {
      const target = document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`);
      if (target) target.innerHTML = element.innerHTML;
    });
  }

  updateURLHash(searchParams) {
    history.pushState({ searchParams }, "", `${window.location.pathname}${searchParams && "?".concat(searchParams)}`);
  }

  onSubmitHandler(event) {
    event.preventDefault();

    if (this.hasAttribute("data-custom-color-filter") &&
        event.target.closest(".js-filter-color-custom")) {
      if (this._colorClickTime && Date.now() - this._colorClickTime < 600) return;
      this.filterBySizeAvailability();
      return;
    }

    const formData = new FormData(event.target.closest("form"));
    const searchParams = new URLSearchParams(formData);

    if (this.hasAttribute("data-custom-color-filter")) {
      const keysToDelete = [];
      for (const [key] of searchParams.entries()) {
        if (key.includes("filter.v.option.color") || key.includes("filter.v.option.couleur")) {
          keysToDelete.push(key);
        }
      }
      keysToDelete.forEach(k => searchParams.delete(k));
    }

    this.renderPage(searchParams.toString(), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    const url = event.currentTarget.dataset.resetUrl.indexOf("?") == -1 ? "" : event.currentTarget.dataset.resetUrl.slice(event.currentTarget.dataset.resetUrl.indexOf("?") + 1);
    this.renderPage(url);
  }

  filterBySizeAvailability() {
    const formData = new FormData(this.querySelector("form"));
    const params = new URLSearchParams(formData);

    let availabilityValues = [];
    let selectedSizes = [];

    for (const [key, value] of params.entries()) {
      if (key.includes("filter.v.availability")) {
        availabilityValues.push(value);
      }
      if (key.includes("filter.v.option.size") || key.includes("filter.v.option.taille")) {
        selectedSizes.push(value);
      }
    }

    this.resetAllProductVisibility();

    const hasAvailabilityFilter = availabilityValues.length > 0;
    const hasSizeFilter = selectedSizes.length > 0;

    if (!hasAvailabilityFilter && !hasSizeFilter) {
      this.filterByColor();
      this.hideEmptyContainers();
      this.updateProductCount();
      return;
    }

    const wantInStock = availabilityValues.includes("1");
    const wantOutOfStock = availabilityValues.includes("0");

    const productCards = document.querySelectorAll("#ProductGridContainer [data-product-id]");
    productCards.forEach((card) => {
      const availableSizes = card.getAttribute("data-available-sizes");
      const availableSizesList = availableSizes
        ? availableSizes.split(",").map((s) => s.trim().toLowerCase().replace(/\(us\)/g, "").trim())
        : [];
      const hasAnyStock = availableSizesList.length > 0;

      if (hasSizeFilter && hasAvailabilityFilter) {
        const matchingSizesInStock = selectedSizes.some(selectedSize => {
          const normalized = selectedSize.trim().toLowerCase().replace(/\(us\)/g, "").trim();
          return availableSizesList.includes(normalized);
        });

        let keep = false;
        if (wantInStock && matchingSizesInStock) keep = true;
        if (wantOutOfStock && !matchingSizesInStock) keep = true;
        if (!keep) card.style.setProperty("display", "none", "important");
      } else if (hasSizeFilter) {
        const hasMatchingSize = selectedSizes.some(selectedSize => {
          const normalized = selectedSize.trim().toLowerCase().replace(/\(us\)/g, "").trim();
          return availableSizesList.includes(normalized);
        });
        if (!hasMatchingSize) card.style.setProperty("display", "none", "important");
      } else if (hasAvailabilityFilter) {
        let keep = false;
        if (wantInStock && hasAnyStock) keep = true;
        if (wantOutOfStock && !hasAnyStock) keep = true;
        if (!keep) card.style.setProperty("display", "none", "important");
      }
    });

    this.filterByColor();
    this.hideEmptyContainers();
    this.updateProductCount();
  }

  resetAllProductVisibility() {
    document.querySelectorAll("#ProductGridContainer [data-product-id]")
      .forEach(card => card.style.removeProperty("display"));
    document.querySelectorAll("#ProductGridContainer product-card")
      .forEach(el => el.style.removeProperty("display"));
    document.querySelectorAll("#ProductGridContainer [data-product-group]")
      .forEach(el => el.style.removeProperty("display"));

    document.querySelectorAll("#ProductGridContainer product-card[data-original-active]").forEach((pc) => {
      const originalHandle = pc.dataset.originalActive;
      const currentActive = pc.querySelector("[data-swatch-content][data-swatch-active]");
      const originalContent = pc.querySelector(`[data-swatch-content="${originalHandle}"]`);

      if (currentActive && originalContent && currentActive !== originalContent) {
        currentActive.classList.add("tw-absolute", "tw-inset-0", "tw-invisible");
        currentActive.removeAttribute("data-swatch-active");
        originalContent.classList.remove("tw-absolute", "tw-inset-0", "tw-invisible");
        originalContent.setAttribute("data-swatch-active", "");

        pc.querySelectorAll("product-card--swatch").forEach((swatch) => {
          if (swatch.dataset.target === originalHandle) {
            swatch.setAttribute("data-selected", "");
          } else {
            swatch.removeAttribute("data-selected");
          }
        });
      }
      pc.removeAttribute("data-original-active");
    });
  }

  cardMatchesColor(card, selectedColors) {
    const productColor = card.dataset.productColor?.trim().toLowerCase();
    const productColorKey = card.dataset.productColorKey?.trim().toLowerCase();
    const productFilterColor = card.dataset.productFilterColor?.trim().toLowerCase();
    const filterColorsList = productFilterColor
      ? productFilterColor.split(",").map(c => c.trim())
      : [];
    return selectedColors.some(sc =>
      productColor === sc ||
      productColorKey === sc ||
      productFilterColor === sc ||
      filterColorsList.includes(sc)
    );
  }

  getDisplayKey(card, selectedColors) {
    if (!card) return null;
    const groupId = card.dataset.productGroupId?.trim();
    let colorKey = null;
    if (selectedColors && selectedColors.length > 0) {
      for (const sc of selectedColors) {
        if (this.cardMatchesColor(card, [sc])) {
          colorKey = sc;
          break;
        }
      }
    }
    if (!colorKey) {
      colorKey = card.dataset.productColorKey?.trim().toLowerCase();
    }
    if (groupId && colorKey) return `${groupId}::${colorKey}`;
    const model = card.dataset.productModel?.trim().toLowerCase();
    if (model && colorKey) return `${model}::${colorKey}`;
    return card.dataset.productId || null;
  }

  filterByColor() {
    if (!this.hasAttribute("data-custom-color-filter")) return;

    const selectedColors = [];
    this.querySelectorAll(".js-filter-color-custom input[type='checkbox']:checked").forEach((cb) => {
      selectedColors.push(cb.value.trim().toLowerCase());
    });

    if (selectedColors.length === 0) return;

    const displayedKeys = new Set();

    const hideAllCards = (pc) => {
      pc.querySelectorAll("[data-product-id]").forEach((card) => {
        if (card.style.display !== "none") {
          card.style.setProperty("display", "none", "important");
        }
      });
    };

    document.querySelectorAll("#ProductGridContainer product-card").forEach((pc) => {
      const swatchContents = pc.querySelectorAll("[data-swatch-content]");
      let activeCard;
      if (swatchContents.length > 1) {
        const activeContent = pc.querySelector("[data-swatch-content][data-swatch-active]");
        activeCard = activeContent ? activeContent.querySelector("[data-product-id]") : null;
      } else {
        activeCard = pc.querySelector("[data-product-id]");
      }

      if (!activeCard) {
        hideAllCards(pc);
        return;
      }

      if (activeCard.style.display === "none") return;

      if (!this.cardMatchesColor(activeCard, selectedColors)) {
        hideAllCards(pc);
        return;
      }

      const key = this.getDisplayKey(activeCard, selectedColors);
      if (key && displayedKeys.has(key)) {
        hideAllCards(pc);
      } else if (key) {
        displayedKeys.add(key);
      }
    });

    document.querySelectorAll("#ProductGridContainer [data-product-id]").forEach((card) => {
      if (card.closest("product-card")) return;
      if (card.style.display === "none") return;
      if (!this.cardMatchesColor(card, selectedColors)) {
        card.style.setProperty("display", "none", "important");
        return;
      }
      const key = this.getDisplayKey(card, selectedColors);
      if (key && displayedKeys.has(key)) {
        card.style.setProperty("display", "none", "important");
      } else if (key) {
        displayedKeys.add(key);
      }
    });
  }

  hideEmptyContainers() {
    document.querySelectorAll("#ProductGridContainer product-card").forEach((pc) => {
      const allCards = pc.querySelectorAll("[data-product-id]");
      let hasUserVisible = false;
      allCards.forEach((card) => {
        if (card.style.display === "none") return;
        const wrapper = card.closest("[data-swatch-content]");
        if (wrapper && wrapper.classList.contains("tw-invisible")) return;
        hasUserVisible = true;
      });
      if (!hasUserVisible) {
        pc.style.setProperty("display", "none", "important");
      }
    });

    document.querySelectorAll("#ProductGridContainer [data-product-group]").forEach((group) => {
      const productCards = group.querySelectorAll("product-card");
      if (productCards.length > 0) {
        const hasVisiblePC = group.querySelector("product-card:not([style*='display: none'])");
        if (!hasVisiblePC) {
          group.style.setProperty("display", "none", "important");
        }
      } else {
        const hasVisibleCard = group.querySelector("[data-product-id]:not([style*='display: none'])");
        if (!hasVisibleCard) {
          group.style.setProperty("display", "none", "important");
        }
      }
    });
  }

  updateProductCount() {
    const productGridHeader = document.getElementById("ProductGridHeader");
    if (!productGridHeader) return;

    const grid = document.getElementById("ProductGridContainer");
    if (!grid) return;

    const productGroups = grid.querySelectorAll("[data-product-group]");
    let visibleCount = 0;

    if (productGroups.length > 0) {
      productGroups.forEach((group) => {
        if (group.style.display !== "none") visibleCount++;
      });
    } else {
      for (const child of grid.children) {
        if (child.style.display === "none") continue;
        if (child.tagName === "PRODUCT-CARD" || child.hasAttribute("data-product-id")) {
          visibleCount++;
        }
      }
    }

    this.renderEmptyState(grid, visibleCount);

    const countElement = productGridHeader.querySelector("span");
    if (countElement) {
      const currentText = countElement.textContent;
      const match = currentText.match(/\d+/);
      if (match) {
        countElement.textContent = currentText.replace(match[0], visibleCount);
      }
    }

    const mobileResults = document.querySelector(".filters-mobile-results");
    if (mobileResults) {
      const mobileText = mobileResults.textContent;
      const mobileMatch = mobileText.match(/\((\d+)\)/);
      if (mobileMatch) {
        const btn = mobileResults.querySelector(".tw-btn");
        if (btn) {
          btn.textContent = btn.textContent.replace(/\(\d+\)/, `(${visibleCount})`);
        }
      }
    }

    this.updateFilterCounts();
  }

  renderEmptyState(grid, visibleCount) {
    const existingEmpty = grid.querySelector("[data-filter-empty-state]");
    if (visibleCount > 0) {
      if (existingEmpty) existingEmpty.remove();
      return;
    }
    if (existingEmpty) return;
    const emptyEl = document.createElement("div");
    emptyEl.setAttribute("data-filter-empty-state", "");
    emptyEl.className = "tw-col-span-full tw-py-12 tw-text-center tw-font-avenir tw-text-black";
    emptyEl.textContent = "Aucun produit ne correspond à votre sélection.";
    grid.appendChild(emptyEl);
  }

  getVisibleCard(container) {
    const cards = container.querySelectorAll("[data-product-id]");
    for (const card of cards) {
      if (card.style.display === "none") continue;
      const wrapper = card.closest("[data-swatch-content]");
      if (wrapper && wrapper.classList.contains("tw-invisible")) continue;
      return card;
    }
    return null;
  }

  updateFilterCounts() {
    const grid = document.getElementById("ProductGridContainer");
    if (!grid) return;

    const productGroups = grid.querySelectorAll("[data-product-group]");
    let visibleProductCards = [];

    if (productGroups.length > 0) {
      productGroups.forEach((group) => {
        if (group.style.display === "none") return;
        const card = this.getVisibleCard(group);
        if (card) visibleProductCards.push(card);
      });
    } else {
      for (const child of grid.children) {
        if (child.style.display === "none") continue;
        if (child.tagName === "PRODUCT-CARD") {
          const card = this.getVisibleCard(child);
          if (card) visibleProductCards.push(card);
        } else if (child.hasAttribute("data-product-id")) {
          visibleProductCards.push(child);
        }
      }
    }

    // Update each filter's value counts
    const filters = this.querySelectorAll(".js-filter");
    filters.forEach((filter) => {
      const inputs = filter.querySelectorAll("input[type='checkbox']");
      
      inputs.forEach((input) => {
        const label = input.closest("label");
        if (!label) return;
        
        const valueText = input.value;
        const paramName = input.name;
        
        // Count how many visible products match this filter value
        let count = 0;
        
        if (paramName.includes("filter.v.availability")) {
          // Availability filter: count products that have any size in stock
          visibleProductCards.forEach((card) => {
            const availableSizes = card.getAttribute("data-available-sizes");
            const hasStock = availableSizes && availableSizes.trim() !== "";
            
            if (valueText === "1" && hasStock) {
              count++;
            } else if (valueText === "0" && !hasStock) {
              count++;
            }
          });
        } else {
          // For other filters, return to avoid breaking existing behavior
          return;
        }
        
        // Update the count in the label
        const countSpan = label.querySelector("span");
        if (countSpan) {
          const countMatch = countSpan.textContent.match(/\((\d+)\)/);
          if (countMatch) {
            countSpan.textContent = countSpan.textContent.replace(/\(\d+\)/, `(${count})`);
          }
        }
      });
    });
  }

}
window.customElements.define("collection-filters-form", CollectionFiltersForm);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    this.querySelector("div").addEventListener("click", (event) => {
      event.preventDefault();
      const form = this.closest("collection-filters-form") || document.querySelector("collection-filters-form");
      form.onActiveFilterClick(event);
    });
  }
}

customElements.define("facet-remove", FacetRemove);
